// Full-screen view of one diagram, image or table. A diagram or image pans and zooms on a
// canvas (drag, wheel, pinch, double-click, keys); a table keeps its natural
// width in a scrolling panel and zooms as a whole. Esc, the close button or a
// click on the backdrop closes it; focus returns to where it came from.

import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type KeyboardEvent as ReactKeyboardEvent,
  type MouseEvent as ReactMouseEvent,
  type PointerEvent as ReactPointerEvent,
} from 'react'
import { createPortal } from 'react-dom'
import { canInteract, usePortalTarget, useTabScope } from '../workspace/context'

export type ZoomTarget =
  | { kind: 'diagram'; svg: SVGSVGElement }
  | { kind: 'image'; src: string; alt: string }
  | { kind: 'table'; table: HTMLTableElement }

interface ZoomOverlayProps {
  target: ZoomTarget
  onClose: () => void
  /** A click on a link inside the copy (a zoomed table's issue links). */
  onLinkClick?: (event: ReactMouseEvent, anchor: HTMLAnchorElement) => void
}

interface View {
  scale: number
  x: number
  y: number
}

interface Point {
  x: number
  y: number
}

/** Safari's trackpad pinch (not in the DOM typings). */
type GestureEvent = Event & { scale: number; clientX: number; clientY: number }

const MIN_SCALE = 0.1
const MAX_SCALE = 8
const STEP = 1.25
const FIT_MAX = 2.5
const FIT_MARGIN = 0.92
/** A wide table opens no smaller than this; Fit shows all of it. */
const TABLE_OPEN_MIN = 0.75
const PAN_STEP = 60
const DRAG_SLOP = 4

const clamp = (scale: number) => Math.min(MAX_SCALE, Math.max(MIN_SCALE, scale))

function naturalSize(svg: SVGSVGElement): { width: number; height: number } {
  const box = svg.getAttribute('viewBox')?.split(/[\s,]+/).map(Number)
  if (box && box.length === 4 && box[2]! > 0 && box[3]! > 0) return { width: box[2]!, height: box[3]! }
  const rect = svg.getBoundingClientRect()
  return { width: rect.width || 800, height: rect.height || 600 }
}

/** Wheel delta in pixels, whatever unit the device reported. */
function wheelPixels(event: WheelEvent, axis: 'x' | 'y'): number {
  const delta = axis === 'x' ? event.deltaX : event.deltaY
  return event.deltaMode === 1 ? delta * 16 : event.deltaMode === 2 ? delta * 400 : delta
}

function focusables(root: HTMLElement): HTMLElement[] {
  return [...root.querySelectorAll<HTMLElement>('button:not([disabled]), [tabindex="0"]')]
}

export function ZoomOverlay({ target, onClose, onLinkClick }: ZoomOverlayProps) {
  const scope = useTabScope()
  const portal = usePortalTarget()
  // A diagram and an image share the canvas; a table scrolls in a panel.
  const isCanvas = target.kind !== 'table'
  const dialogRef = useRef<HTMLDivElement>(null)
  const stageRef = useRef<HTMLDivElement>(null)
  const contentRef = useRef<HTMLDivElement>(null)
  const [view, setView] = useState<View>({ scale: 1, x: 0, y: 0 })
  const viewRef = useRef(view)
  const natural = useRef({ width: 0, height: 0 })
  const pointers = useRef(new Map<number, Point>())
  const dragDistance = useRef(0)
  const pressedOn = useRef<EventTarget | null>(null)
  const gestureScale = useRef<number | null>(null)

  useLayoutEffect(() => {
    viewRef.current = view
  }, [view])

  const stagePoint = useCallback((clientX: number, clientY: number): Point => {
    const rect = stageRef.current?.getBoundingClientRect()
    return { x: clientX - (rect?.left ?? 0), y: clientY - (rect?.top ?? 0) }
  }, [])

  const stageCentre = useCallback((): Point => {
    const rect = stageRef.current?.getBoundingClientRect()
    return { x: (rect?.width ?? 0) / 2, y: (rect?.height ?? 0) / 2 }
  }, [])

  /** Scales by `factor` keeping the stage point `at` still (diagram), or as a whole (table). */
  const zoomAt = useCallback((at: Point, factor: number) => {
    setView((v) => {
      const scale = clamp(v.scale * factor)
      if (scale === v.scale) return v
      const ratio = scale / v.scale
      return { scale, x: at.x - ratio * (at.x - v.x), y: at.y - ratio * (at.y - v.y) }
    })
  }, [])

  const pan = useCallback((dx: number, dy: number) => {
    if (dx || dy) setView((v) => ({ ...v, x: v.x + dx, y: v.y + dy }))
  }, [])

  const fit = useCallback((opening = false) => {
    const stage = stageRef.current?.getBoundingClientRect()
    const { width, height } = natural.current
    if (!stage || !stage.width || !stage.height || !width || !height) {
      setView({ scale: 1, x: 0, y: 0 })
      return
    }
    if (isCanvas) {
      const scale = clamp(Math.min(FIT_MAX, Math.min(stage.width / width, stage.height / height) * FIT_MARGIN))
      setView({ scale, x: (stage.width - width * scale) / 2, y: (stage.height - height * scale) / 2 })
    } else {
      // A table fits its width only (never enlarged); its height scrolls.
      const element = stageRef.current!
      const style = getComputedStyle(element)
      const room = element.clientWidth - (parseFloat(style.paddingLeft) || 0) - (parseFloat(style.paddingRight) || 0) - 2
      const scale = Math.min(1, room / width)
      setView({ scale: clamp(opening ? Math.max(TABLE_OPEN_MIN, scale) : scale), x: 0, y: 0 })
    }
  }, [isCanvas])

  // Mount the copy of the diagram or table, then fit it.
  useLayoutEffect(() => {
    const host = contentRef.current
    if (!host) return
    if (target.kind === 'diagram') {
      const size = naturalSize(target.svg)
      const svg = target.svg.cloneNode(true) as SVGSVGElement
      svg.removeAttribute('style')
      svg.setAttribute('width', String(size.width))
      svg.setAttribute('height', String(size.height))
      host.replaceChildren(svg)
      const box = host.getBoundingClientRect()
      natural.current = { width: box.width || size.width, height: box.height || size.height }
    } else if (target.kind === 'image') {
      const img = document.createElement('img')
      img.alt = target.alt
      img.draggable = false
      const settle = () => {
        const box = host.getBoundingClientRect()
        natural.current = { width: box.width, height: box.height }
        fit(true)
      }
      img.addEventListener('load', settle)
      img.src = target.src
      host.replaceChildren(img)
      // A cached image may be complete before the listener could hear it.
      if (img.complete && img.naturalWidth > 0) settle()
      return () => img.removeEventListener('load', settle)
    } else {
      host.replaceChildren(target.table.cloneNode(true))
      natural.current = { width: host.scrollWidth, height: host.scrollHeight }
    }
    fit(true)
  }, [target, fit, portal])

  // Focus moves into the dialog and back to the trigger; the page stops scrolling.
  useEffect(() => {
    if (!portal) return
    const previous = document.activeElement instanceof HTMLElement ? document.activeElement : null
    const surface = scope?.surface() ?? document.body
    const overflow = surface.style.overflow
    surface.style.overflow = 'hidden'
    if (canInteract(scope)) dialogRef.current?.focus({ preventScroll: true })
    return () => {
      surface.style.overflow = overflow
      if (canInteract(scope) && previous?.isConnected) previous.focus({ preventScroll: true })
    }
  }, [portal])

  // Wheel and Safari's gesture events need non-passive native listeners.
  useEffect(() => {
    const stage = stageRef.current
    if (!stage) return
    const onWheel = (event: WheelEvent) => {
      const zoom = event.ctrlKey || event.metaKey
      if (!isCanvas && !zoom) return // a table scrolls natively
      event.preventDefault()
      if (zoom) {
        if (gestureScale.current !== null) return // Safari reports the pinch as a gesture too
        const dy = Math.max(-50, Math.min(50, wheelPixels(event, 'y')))
        zoomAt(stagePoint(event.clientX, event.clientY), Math.exp(-dy * 0.01))
      } else {
        pan(-wheelPixels(event, 'x'), -wheelPixels(event, 'y'))
      }
    }
    const onGestureStart = (event: Event) => {
      event.preventDefault()
      gestureScale.current = viewRef.current.scale
    }
    const onGestureChange = (event: Event) => {
      event.preventDefault()
      const start = gestureScale.current
      if (start === null) return
      const gesture = event as GestureEvent
      zoomAt(stagePoint(gesture.clientX, gesture.clientY), clamp(start * gesture.scale) / viewRef.current.scale)
    }
    const onGestureEnd = (event: Event) => {
      event.preventDefault()
      gestureScale.current = null
    }
    stage.addEventListener('wheel', onWheel, { passive: false })
    stage.addEventListener('gesturestart', onGestureStart)
    stage.addEventListener('gesturechange', onGestureChange)
    stage.addEventListener('gestureend', onGestureEnd)
    return () => {
      stage.removeEventListener('wheel', onWheel)
      stage.removeEventListener('gesturestart', onGestureStart)
      stage.removeEventListener('gesturechange', onGestureChange)
      stage.removeEventListener('gestureend', onGestureEnd)
    }
  }, [isCanvas, pan, stagePoint, zoomAt, portal])

  const onPointerDown = (event: ReactPointerEvent<HTMLDivElement>) => {
    if (event.pointerType === 'mouse' && event.button !== 0) return
    if (!isCanvas && event.pointerType === 'mouse') return // mouse selects text in a table
    if (pointers.current.size === 0) dragDistance.current = 0
    pointers.current.set(event.pointerId, { x: event.clientX, y: event.clientY })
    // A diagram keeps its drag when the pointer leaves the stage. A table
    // never captures: capture would retarget a tap on one of its links.
    if (isCanvas) event.currentTarget.setPointerCapture?.(event.pointerId)
  }

  const onPointerMove = (event: ReactPointerEvent<HTMLDivElement>) => {
    const previous = pointers.current.get(event.pointerId)
    if (!previous) return
    const next = { x: event.clientX, y: event.clientY }
    if (pointers.current.size === 1 && isCanvas) {
      dragDistance.current += Math.hypot(next.x - previous.x, next.y - previous.y)
      pan(next.x - previous.x, next.y - previous.y)
    } else if (pointers.current.size === 2) {
      const other = [...pointers.current].find(([id]) => id !== event.pointerId)?.[1]
      if (other) {
        const before = Math.hypot(previous.x - other.x, previous.y - other.y)
        const after = Math.hypot(next.x - other.x, next.y - other.y)
        const midBefore = { x: (previous.x + other.x) / 2, y: (previous.y + other.y) / 2 }
        const midAfter = { x: (next.x + other.x) / 2, y: (next.y + other.y) / 2 }
        dragDistance.current += DRAG_SLOP
        if (before > 0) zoomAt(stagePoint(midAfter.x, midAfter.y), after / before)
        if (isCanvas) pan(midAfter.x - midBefore.x, midAfter.y - midBefore.y)
      }
    }
    pointers.current.set(event.pointerId, next)
  }

  const onPointerEnd = (event: ReactPointerEvent<HTMLDivElement>) => {
    pointers.current.delete(event.pointerId)
  }

  const onDoubleClick = (event: ReactMouseEvent<HTMLDivElement>) => {
    if (!isCanvas) return
    event.preventDefault()
    if (viewRef.current.scale >= 1.5) fit()
    else zoomAt(stagePoint(event.clientX, event.clientY), 2 / viewRef.current.scale)
  }

  // A press and release on the backdrop closes; the end of a drag or pinch,
  // or a click on the diagram or table itself, does not. (Pointer capture
  // retargets the click to the stage, so the press decides.)
  const isBackdrop = (node: EventTarget | null) => node === dialogRef.current || node === stageRef.current
  const onClick = (event: ReactMouseEvent<HTMLDivElement>) => {
    // Clicks stay inside the overlay: they must not reach the document's
    // own handlers through the React tree.
    event.stopPropagation()
    const anchor = (event.target as Element).closest?.('a[href]')
    if (anchor instanceof HTMLAnchorElement && contentRef.current?.contains(anchor)) {
      onLinkClick?.(event, anchor)
      return
    }
    const dragged = dragDistance.current > DRAG_SLOP
    dragDistance.current = 0
    if (!dragged && isBackdrop(pressedOn.current) && isBackdrop(event.target)) onClose()
  }

  const onKeyDown = (event: ReactKeyboardEvent<HTMLDivElement>) => {
    const handled = () => {
      event.preventDefault()
      event.stopPropagation()
    }
    if (event.key === 'Escape') {
      handled()
      onClose()
      return
    }
    if (event.key === 'Tab') {
      const items = focusables(event.currentTarget)
      if (items.length === 0) return
      const first = items[0]!
      const last = items[items.length - 1]!
      const active = document.activeElement
      if (event.shiftKey && (active === first || active === event.currentTarget)) {
        handled()
        last.focus()
      } else if (!event.shiftKey && active === last) {
        handled()
        first.focus()
      }
      return
    }
    if (event.metaKey || event.ctrlKey || event.altKey) return
    switch (event.key) {
      case '+':
      case '=':
        handled()
        zoomAt(stageCentre(), STEP)
        return
      case '-':
      case '_':
        handled()
        zoomAt(stageCentre(), 1 / STEP)
        return
      case '0':
        handled()
        fit()
        return
    }
    if (!isCanvas) return // arrows scroll the table
    const arrows: Record<string, [number, number]> = {
      ArrowLeft: [PAN_STEP, 0],
      ArrowRight: [-PAN_STEP, 0],
      ArrowUp: [0, PAN_STEP],
      ArrowDown: [0, -PAN_STEP],
    }
    const step = arrows[event.key]
    if (step) {
      handled()
      pan(step[0], step[1])
    }
  }

  const label = target.kind === 'diagram' ? 'Diagram' : target.kind === 'image' ? 'Image' : 'Table'
  const contentStyle = isCanvas
    ? { transform: `translate(${view.x}px, ${view.y}px) scale(${view.scale})` }
    : { zoom: view.scale }

  return portal ? createPortal(
    <div
      ref={dialogRef}
      className={`markdown-body md-overlay md-overlay--${target.kind}`}
      role="dialog"
      aria-modal="true"
      aria-label={label}
      tabIndex={-1}
      onKeyDown={onKeyDown}
      onPointerDownCapture={(event) => {
        pressedOn.current = event.target
      }}
      onClick={onClick}
    >
      <div className="md-overlay-toolbar" role="group" aria-label={`${label} zoom`}>
        <button type="button" className="md-overlay-button" aria-label="Zoom out" title="Zoom out (−)" onClick={() => zoomAt(stageCentre(), 1 / STEP)}>
          −
        </button>
        <span className="md-overlay-level" aria-live="polite">
          {Math.round(view.scale * 100)}%
        </span>
        <button type="button" className="md-overlay-button" aria-label="Zoom in" title="Zoom in (+)" onClick={() => zoomAt(stageCentre(), STEP)}>
          +
        </button>
        <button type="button" className="md-overlay-button md-overlay-fit" aria-label={isCanvas ? 'Fit to screen' : 'Fit to width'} title="Fit (0)" onClick={() => fit()}>
          Fit
        </button>
        <button type="button" className="md-overlay-button md-overlay-close" aria-label="Close" title="Close (Esc)" onClick={onClose}>
          ×
        </button>
      </div>
      <div
        ref={stageRef}
        className="md-overlay-stage"
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerEnd}
        onPointerCancel={onPointerEnd}
        onDoubleClick={onDoubleClick}
      >
        {isCanvas ? (
          <div ref={contentRef} className="md-overlay-canvas" style={contentStyle} />
        ) : (
          <div className="md-overlay-panel" tabIndex={0}>
            <div ref={contentRef} className="md-overlay-table" style={contentStyle} />
          </div>
        )}
      </div>
    </div>,
    portal,
  ) : null
}
